import React, { useState } from 'react';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    alpha,
    Button,
    CircularProgress,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import useTheme from '@mui/system/useTheme';
import { GameLogEntry } from 'api/BaseballRecruits';

interface BaseballGameLogProps {
    games: GameLogEntry[];
    playerName: string;
    onRefresh?: () => Promise<void> | void;
}

const HEADER_SX = { fontWeight: 700, fontSize: '0.8rem', py: 1.25 } as const;
const HEADER_CENTER_SX = { ...HEADER_SX, textAlign: 'center' } as const;
const CELL_SX = { fontSize: '0.85rem', py: 1 } as const;
const CELL_CENTER_SX = { ...CELL_SX, textAlign: 'center' } as const;
const TOTALS_SX = { fontSize: '0.9rem', fontWeight: 700, py: 1.25 } as const;
const TOTALS_CENTER = { ...TOTALS_SX, textAlign: 'center' } as const;

const sumStat = (games: GameLogEntry[], key: string): number =>
    games.reduce((sum, g) => sum + parseInt(g.batting?.[key] || '0', 10), 0);

const BaseballGameLog: React.FC<BaseballGameLogProps> = ({ games, playerName, onRefresh }) => {
    const theme = useTheme();
    const [localRefreshing, setLocalRefreshing] = useState(false);

    const handleRefresh = async () => {
        if (!onRefresh || localRefreshing) return;
        setLocalRefreshing(true);
        try {
            await onRefresh();
        } finally {
            setLocalRefreshing(false);
        }
    };

    if (!games || games.length === 0) {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="text.secondary" sx={{ mb: onRefresh ? 1 : 0 }}>
                    No game log data available for {playerName}
                </Typography>
                {onRefresh && (
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={
                            localRefreshing ? <CircularProgress size={16} /> : <RefreshIcon />
                        }
                        disabled={localRefreshing}
                        onClick={handleRefresh}
                    >
                        {localRefreshing ? 'Refreshing…' : 'Refresh Data'}
                    </Button>
                )}
            </Box>
        );
    }

    const hasAnyBattingStats = games.some((g) => g.batting && Object.keys(g.batting).length > 0);
    const hasAnyResults = games.some((g) => g.result);

    const totals = hasAnyBattingStats
        ? {
              GP: games.filter((g) => g.batting && Object.keys(g.batting).length > 0).length,
              AB: sumStat(games, 'AB'),
              H: sumStat(games, 'H'),
              R: sumStat(games, 'R'),
              RBI: sumStat(games, 'RBI'),
              BB: sumStat(games, 'BB'),
              K: sumStat(games, 'K'),
              HR: sumStat(games, 'HR'),
          }
        : null;
    const totalsAvg = totals && totals.AB > 0 ? (totals.H / totals.AB).toFixed(3) : '-';

    const getResultColor = (result?: string) => {
        if (!result) return 'default';
        const r = result.toUpperCase();
        if (r === 'W' || r.startsWith('W')) return 'success';
        if (r === 'L' || r.startsWith('L')) return 'error';
        return 'default';
    };

    const getHitHighlight = (batting?: Record<string, string | undefined>) => {
        if (!batting) return 'none';
        const hits = parseInt(batting.H || '0', 10);
        const ab = parseInt(batting.AB || '0', 10);
        if (ab === 0) return 'none';
        const avg = hits / ab;
        if (avg >= 0.5 && hits >= 2) return 'hot';
        if (hits >= 1) return 'warm';
        if (hits === 0 && ab >= 3) return 'cold';
        return 'none';
    };

    const formatHomeAway = (ha?: string) => {
        if (!ha) return '';
        const lower = ha.toLowerCase();
        if (lower === 'away') return '@ ';
        if (lower === 'home') return 'vs ';
        return '';
    };

    const metaColCount = 2 + (hasAnyResults ? 1 : 0);

    return (
        <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                Game Log — {playerName}
            </Typography>
            {!hasAnyBattingStats && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                        Per-game batting stats not yet available.
                    </Typography>
                    {onRefresh && (
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={
                                localRefreshing ? (
                                    <CircularProgress size={14} />
                                ) : (
                                    <RefreshIcon sx={{ fontSize: 16 }} />
                                )
                            }
                            disabled={localRefreshing}
                            onClick={handleRefresh}
                            sx={{ fontSize: '0.75rem', py: 0.25, px: 1, minWidth: 0 }}
                        >
                            {localRefreshing ? 'Refreshing…' : 'Refresh'}
                        </Button>
                    )}
                </Box>
            )}
            <TableContainer>
                <Table size="medium">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={HEADER_SX}>Date</TableCell>
                            <TableCell sx={HEADER_SX}>Opponent</TableCell>
                            {hasAnyResults && <TableCell sx={HEADER_SX}>Result</TableCell>}
                            {hasAnyBattingStats && (
                                <>
                                    <TableCell sx={HEADER_CENTER_SX}>AB</TableCell>
                                    <TableCell sx={HEADER_CENTER_SX}>H</TableCell>
                                    <TableCell sx={HEADER_CENTER_SX}>R</TableCell>
                                    <TableCell sx={HEADER_CENTER_SX}>RBI</TableCell>
                                    <TableCell sx={HEADER_CENTER_SX}>BB</TableCell>
                                    <TableCell sx={HEADER_CENTER_SX}>K</TableCell>
                                    <TableCell sx={HEADER_CENTER_SX}>HR</TableCell>
                                </>
                            )}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {games.map((game, index) => {
                            const highlight = getHitHighlight(game.batting);
                            let rowBg = 'transparent';
                            if (highlight === 'hot')
                                rowBg = alpha(theme.palette.success.main, 0.06);
                            else if (highlight === 'cold')
                                rowBg = alpha(theme.palette.error.main, 0.04);

                            return (
                                <TableRow key={index} sx={{ backgroundColor: rowBg }}>
                                    <TableCell sx={{ ...CELL_SX, whiteSpace: 'nowrap' }}>
                                        {game.date || '-'}
                                    </TableCell>
                                    <TableCell sx={CELL_SX}>
                                        {formatHomeAway(game.home_away)}
                                        {game.opponent || '-'}
                                    </TableCell>
                                    {hasAnyResults && (
                                        <TableCell>
                                            {game.result ? (
                                                <Chip
                                                    label={`${game.result}${
                                                        game.score ? ` ${game.score}` : ''
                                                    }`}
                                                    size="small"
                                                    color={getResultColor(game.result) as any}
                                                    variant="outlined"
                                                    sx={{ height: 24, fontSize: '0.75rem' }}
                                                />
                                            ) : (
                                                '-'
                                            )}
                                        </TableCell>
                                    )}
                                    {hasAnyBattingStats && (
                                        <>
                                            <TableCell sx={CELL_CENTER_SX}>
                                                {game.batting?.AB || '-'}
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    ...CELL_CENTER_SX,
                                                    fontWeight: highlight === 'hot' ? 700 : 400,
                                                    color:
                                                        highlight === 'hot'
                                                            ? theme.palette.success.main
                                                            : highlight === 'cold'
                                                            ? theme.palette.error.main
                                                            : 'inherit',
                                                }}
                                            >
                                                {game.batting?.H || '-'}
                                            </TableCell>
                                            <TableCell sx={CELL_CENTER_SX}>
                                                {game.batting?.R || '-'}
                                            </TableCell>
                                            <TableCell sx={CELL_CENTER_SX}>
                                                {game.batting?.RBI || '-'}
                                            </TableCell>
                                            <TableCell sx={CELL_CENTER_SX}>
                                                {game.batting?.BB || '-'}
                                            </TableCell>
                                            <TableCell sx={CELL_CENTER_SX}>
                                                {game.batting?.K || '-'}
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    ...CELL_CENTER_SX,
                                                    fontWeight:
                                                        parseInt(game.batting?.HR || '0', 10) > 0
                                                            ? 700
                                                            : 400,
                                                    color:
                                                        parseInt(game.batting?.HR || '0', 10) > 0
                                                            ? theme.palette.success.main
                                                            : 'inherit',
                                                }}
                                            >
                                                {game.batting?.HR || '-'}
                                            </TableCell>
                                        </>
                                    )}
                                </TableRow>
                            );
                        })}
                        {totals && (
                            <TableRow
                                sx={{
                                    backgroundColor: alpha(theme.palette.primary.main, 0.04),
                                    borderTop: `2px solid ${theme.palette.divider}`,
                                }}
                            >
                                <TableCell sx={TOTALS_SX} colSpan={metaColCount}>
                                    Totals ({totals.GP} G) · {totalsAvg} AVG
                                </TableCell>
                                <TableCell sx={TOTALS_CENTER}>{totals.AB}</TableCell>
                                <TableCell sx={TOTALS_CENTER}>{totals.H}</TableCell>
                                <TableCell sx={TOTALS_CENTER}>{totals.R}</TableCell>
                                <TableCell sx={TOTALS_CENTER}>{totals.RBI}</TableCell>
                                <TableCell sx={TOTALS_CENTER}>{totals.BB}</TableCell>
                                <TableCell sx={TOTALS_CENTER}>{totals.K}</TableCell>
                                <TableCell sx={TOTALS_CENTER}>{totals.HR}</TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default BaseballGameLog;
